import { useState } from 'react'
import type { FolderResponse } from '../api/types'
import { errorMessage } from '../lib/format'

/**
 * "Move to…": a destination picked from the room's folders (the root included), then
 * one button. Used for a document and for a folder; the caller supplies the mutation.
 * The server decides whether the move is allowed (edit_metadata here, upload there,
 * never a folder into its own subtree); this only offers the list.
 */
export function MoveTo({
  folders,
  currentId,
  excludeSubtreeOf,
  onMove,
  pending,
  error,
}: {
  folders: FolderResponse[]
  /** Where the item is now, so it is not offered. */
  currentId: string | null
  /** For a folder: itself and everything beneath it are not destinations. */
  excludeSubtreeOf?: string
  onMove: (destinationId: string | null) => void
  pending: boolean
  error: unknown
}) {
  const [destination, setDestination] = useState<string>('')
  const excluded = new Set<string>()
  if (excludeSubtreeOf) {
    excluded.add(excludeSubtreeOf)
    let grew = true
    while (grew) {
      grew = false
      for (const f of folders) {
        if (f.parentId && excluded.has(f.parentId) && !excluded.has(f.id)) {
          excluded.add(f.id)
          grew = true
        }
      }
    }
  }
  const options = folders
    .filter((f) => !excluded.has(f.id) && f.id !== currentId)
    .sort((a, b) => a.indexNumber.localeCompare(b.indexNumber, undefined, { numeric: true }))

  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault()
        if (destination === '') return
        onMove(destination === 'root' ? null : destination)
      }}
    >
      <select
        value={destination}
        onChange={(e) => setDestination(e.target.value)}
        aria-label="Move to"
      >
        <option value="">Move to…</option>
        {currentId !== null && <option value="root">Room root</option>}
        {options.map((f) => (
          <option key={f.id} value={f.id}>
            {f.indexNumber} {f.name}
          </option>
        ))}
      </select>
      <button type="submit" className="btn btn-sm" disabled={destination === '' || pending}>
        Move
      </button>
      {error != null && <span className="error-text small">{errorMessage(error)}</span>}
    </form>
  )
}
